import { useQuery } from '@tanstack/react-query';
import { Loader2, X, MapPin, Send, Info, CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';

/**
 * What a pickup request actually is, and what actually went to the courier.
 *
 * The screen invites the wrong expectation — people look for a "from and to" the way an order has
 * one. A pickup is a COLLECTION: there is no destination, and the collection address is not
 * transmitted either. Delhivery's pickup call carries four fields, and the only thing identifying
 * the place is `client_warehouse`, a NAME they resolve against their own records.
 *
 * So this shows three things in the order they matter: the name that went on the wire and where it
 * came from, the address we hold behind that name (to check the name points at the right dock), and
 * the request and reply exactly as logged.
 */
export function PickupDetailDialog({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['pickup-request', id],
    queryFn: async () => (await api.get(`/pickup-requests/${id}`)).data as any,
    enabled: !!id,
  });
  if (!id) return null;

  const loc = data?.courierLocationName;
  // Informational, NOT a warning: most confirmed pickups have no mapping on our side, because the
  // mapping lives at the courier. Styling this red would cry wolf on the majority of rows.
  const noMapping = loc ? loc.hasMapping === false : false;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">Pickup request #{id}</h3>
            <p className="text-xs text-slate-500">What was sent to the courier, and the address behind it</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {isLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900">
              A pickup request is a <b>collection</b>, so it has no destination address — and the pickup address is
              not sent either. The courier is given the <b>pickup location name</b> and resolves it against the
              warehouse registered on their side.
            </div>

            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><Send size={13} /> Name sent to the courier</div>
              <div className="border border-slate-200 rounded-lg px-3 py-2.5">
                <div className="font-mono text-sm text-slate-900">{loc?.sent ?? '—'}</div>
                <div className={`text-[11px] mt-0.5 flex items-center gap-1 ${noMapping ? 'text-slate-600' : 'text-emerald-700'}`}>
                  {noMapping ? <Info size={11} /> : <CheckCircle2 size={11} />} {loc?.source ?? '—'}
                </div>
                {noMapping && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Normal — the mapping lives with the courier, not with us. The collection works as long as they have
                    a warehouse registered under exactly this name. If a request is refused, this is the first thing to check.
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><MapPin size={13} /> Pickup address on our record</div>
              {data?.pickupAddress ? (
                <div className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                  <div className="font-medium text-slate-800">{data.pickupAddress.name}</div>
                  <div className="text-slate-600">{data.pickupAddress.fullAddress || '—'}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {data.pickupAddress.contactName || '—'}{data.pickupAddress.contactPhone ? ` · ${data.pickupAddress.contactPhone}` : ''}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">Held by us for reference — not part of the pickup request.</div>
                </div>
              ) : <div className="text-sm text-slate-400">No warehouse linked to this request.</div>}
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">Exactly what went to the courier</div>
              {(data?.courierExchange ?? []).length ? (
                <div className="space-y-2">
                  {data.courierExchange.map((x: any) => (
                    <div key={x.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-slate-50 text-[11px]">
                        <span className="font-medium text-slate-700">{x.action}</span>
                        <span className={`px-1.5 py-0.5 rounded ${x.isError ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>HTTP {x.httpStatus ?? '—'}</span>
                        <span className="text-slate-500">{x.at ? new Date(x.at).toLocaleString('en-IN') : ''}</span>
                        <span className="text-slate-400 truncate ml-auto max-w-[45%]" title={x.url}>{x.url}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        <div className="p-2.5">
                          <div className="text-[10px] uppercase text-slate-400 mb-1">Sent</div>
                          <pre className="text-[11px] text-slate-800 whitespace-pre-wrap break-all font-mono">{fmt(x.request)}</pre>
                        </div>
                        <div className="p-2.5">
                          <div className="text-[10px] uppercase text-slate-400 mb-1">Reply</div>
                          <pre className="text-[11px] text-slate-800 whitespace-pre-wrap break-all font-mono">{fmt(x.response)}</pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-400">
                  Nothing was sent to a courier for this request — it was recorded here only.
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(v: any): string {
  if (v == null) return '—';
  try { return JSON.stringify(typeof v === 'string' ? JSON.parse(v) : v, null, 2); }
  catch { return String(v); }
}
